<script setup>
// Onboarding first-patch step (MDL-328). Lets the customer opt into a starter
// bounce modulation: pick a device (MIDI channel) and a parameter (CC), and on
// Finish the editor creates the patch, pushes it and sets it active — so the
// device is already modulating when they land on the editor. See spec §2.4.
//
// The step mutates a `draft` object owned by the editor:
//   { enabled, channel, cc, min, max, rate }
// Creation is gated purely on `draft.enabled`, which defaults false — so
// skipping this step (or the whole wizard) falls back to the empty scaffold
// seed with no surprise modulation.
//
// Channel/CC selection deliberately mirrors the normal per-effect routing
// picker (Effect.vue channel_names()/ccLabel()) — same field labels ("Channel"
// / "CC"), same option formatting, and the CC list is populated from the
// chosen device's own labelled CC mappings (settings.devices[i].cc) rather
// than a free-typed number, so a customer picks "5 - Mix" instead of guessing
// a CC number blind.

import { computed } from 'vue';

const props = defineProps({
  // The device config's `settings` — used to label channels/CCs with the
  // names set in the MIDI step.
  settings: {
    type: Object,
    required: true,
  },
  // Reactive draft owned by the editor; this step mutates it in place.
  draft: {
    type: Object,
    required: true,
  },
});

// Channel options 1–16, labelled with the device name where one is set —
// same "N - Name" format as Effect.vue's channel_names().
const channelOptions = computed(() =>
  Array.from({ length: 16 }, (_, i) => {
    const ch = i + 1;
    const name = props.settings?.devices?.[i]?.name?.trim();
    return { value: ch, label: name ? `${ch} - ${name}` : `${ch}` };
  }),
);

// CC options for the chosen channel's device, labelled from its known
// mappings (settings.devices[i].cc) — same "N - Label" format as Effect.vue's
// ccLabel(), including its 1-128 numbering convention.
const ccOptions = computed(() => {
  const ccLabels = props.settings?.devices?.[props.draft.channel - 1]?.cc ?? {};
  return Array.from({ length: 128 }, (_, i) => {
    const cc = i + 1;
    const label = ccLabels[cc];
    return { value: cc, label: label ? `${cc} - ${label}` : `${cc}` };
  });
});
</script>

<template>
  <div class="space-y-4">
    <p class="text-base-content/80">
      Add your first modulation so you can hear Midulate working. Pick a pedal
      and a parameter to move — a mix or filter level works well — and we'll add
      a gentle bounce.
    </p>

    <!-- A plain flex row, not daisyUI's .label (white-space: nowrap) — that
         class is meant for short single-line captions and doesn't handle
         this sentence wrapping gracefully. items-start keeps the toggle
         aligned with the first line if it wraps to two. -->
    <label class="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        class="toggle toggle-primary"
        data-testid="first-patch-enable"
        v-model="draft.enabled"
      />
      <span>Add a starter bounce modulation <span class="text-base-content/60">(recommended)</span></span>
    </label>

    <template v-if="draft.enabled">
      <!-- daisyUI 5 dropped .form-control (it was a v4 class) — a
           <label class="form-control"> here has no layout CSS at all, so it
           falls back to the browser default (display: inline), and whether
           the caption ends up above or beside the select is just an accident
           of how much room the viewport happens to leave for inline wrapping.
           .fieldset (a real, still-supported daisyUI 5 component — used the
           same way in WifiForm/DeviceSettingsForm) is a single-column grid,
           so the legend sits above the control unconditionally. -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Channel</legend>
          <select class="select select-bordered" data-testid="first-patch-channel" v-model.number="draft.channel">
            <option v-for="opt in channelOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </fieldset>

        <fieldset class="fieldset">
          <legend class="fieldset-legend">CC</legend>
          <select class="select select-bordered" data-testid="first-patch-cc" v-model.number="draft.cc">
            <option v-for="opt in ccOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </fieldset>
      </div>

      <div role="note" class="alert" data-testid="first-patch-verify-note">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>When you finish, this bounce starts running — watch the value move
          on your pedal (or in the editor) to confirm it's working.</span>
      </div>
    </template>
  </div>
</template>
